/**
 * Auto-detection utility for phone numbers and email addresses
 * Automatically detects and formats phone numbers and email addresses
 */

// Phone number patterns for different countries
const PHONE_PATTERNS = {
  // India
  "+91": /^(\+91)?[\s-]?[6-9]\d{9}$/,
  // US/Canada
  "+1": /^(\+1)?[\s-]?\(?[2-9]\d{2}\)?[\s-]?\d{3}[\s-]?\d{4}$/,
  // UK
  "+44": /^(\+44)?[\s-]?[1-9]\d{8,9}$/,
  // Australia
  "+61": /^(\+61)?[\s-]?[2-8]\d{8}$/,
  // Generic international
  generic: /^(\+\d{1,4})?[\s-]?\d{6,15}$/,
};

// Email pattern
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Detects if input is a phone number or email
 * @param {string} input - User input
 * @returns {Object} Detection result
 */
export const detectContactType = (input) => {
  if (!input || typeof input !== "string") {
    return { type: "unknown", value: "", countryCode: "", phone: "" };
  }

  const trimmedInput = input.trim();

  // Check if it's an email
  if (EMAIL_PATTERN.test(trimmedInput)) {
    return {
      type: "email",
      value: trimmedInput.toLowerCase(),
      email: trimmedInput.toLowerCase(),
      phone: "",
      countryCode: "",
    };
  }

  // Check if it's a phone number
  const phoneResult = detectPhoneNumber(trimmedInput);
  if (phoneResult.type === "phone") {
    return phoneResult;
  }

  // If no pattern matches, return unknown
  return {
    type: "unknown",
    value: trimmedInput,
    phone: "",
    countryCode: "",
    email: "",
  };
};

/**
 * Detects and formats phone number
 * @param {string} input - Phone number input
 * @returns {Object} Phone detection result
 */
export const detectPhoneNumber = (input) => {
  if (!input || typeof input !== "string") {
    return { type: "unknown", value: "", countryCode: "", phone: "" };
  }

  const trimmedInput = input.trim();

  // Remove all non-digit characters except + at the beginning
  const cleanInput = trimmedInput.replace(/[^\d+]/g, "");

  // Check for different country codes
  const countryCodes = ["+91", "+1", "+44", "+61"];

  for (const countryCode of countryCodes) {
    if (cleanInput.startsWith(countryCode)) {
      const phoneNumber = cleanInput.substring(countryCode.length);
      if (phoneNumber.length >= 6 && phoneNumber.length <= 15) {
        return {
          type: "phone",
          value: `${countryCode} ${phoneNumber}`,
          countryCode: countryCode,
          phone: phoneNumber,
          email: "",
        };
      }
    }
  }

  // Check if it's a local number (no country code)
  // For Indian numbers, must be exactly 10 digits starting with 6-9
  if (/^[6-9]\d{9}$/.test(cleanInput)) {
    return {
      type: "phone",
      value: `+91 ${cleanInput}`,
      countryCode: "+91",
      phone: cleanInput,
      email: "",
    };
  }

  // For other international numbers (6-15 digits)
  if (/^\d{6,15}$/.test(cleanInput)) {
    // Default to India (+91) for local numbers
    return {
      type: "phone",
      value: `+91 ${cleanInput}`,
      countryCode: "+91",
      phone: cleanInput,
      email: "",
    };
  }

  // Check if it starts with + but no known country code
  if (cleanInput.startsWith("+") && cleanInput.length >= 7) {
    const phoneNumber = cleanInput.substring(1);
    return {
      type: "phone",
      value: `+${phoneNumber}`,
      countryCode: "+",
      phone: phoneNumber,
      email: "",
    };
  }

  return {
    type: "unknown",
    value: trimmedInput,
    phone: "",
    countryCode: "",
    email: "",
  };
};

/**
 * Formats phone number for display
 * @param {string} phone - Phone number
 * @param {string} countryCode - Country code
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone, countryCode = "+91") => {
  if (!phone) return "";

  // Remove all non-digit characters
  const cleanPhone = phone.replace(/\D/g, "");

  // Format based on country code
  switch (countryCode) {
    case "+91": // India
      if (cleanPhone.length === 10) {
        return `${cleanPhone.slice(0, 5)} ${cleanPhone.slice(5)}`;
      }
      break;
    case "+1": // US/Canada
      if (cleanPhone.length === 10) {
        return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(
          3,
          6
        )}-${cleanPhone.slice(6)}`;
      }
      break;
    case "+44": // UK
      if (cleanPhone.length >= 10) {
        return `${cleanPhone.slice(0, 5)} ${cleanPhone.slice(5)}`;
      }
      break;
    default:
      return cleanPhone;
  }

  return cleanPhone;
};

/**
 * Validates phone number format
 * @param {string} phone - Phone number
 * @param {string} countryCode - Country code
 * @returns {boolean} Is valid phone number
 */
export const validatePhoneNumber = (phone, countryCode = "+91") => {
  if (!phone) return false;

  const cleanPhone = phone.replace(/\D/g, "");

  switch (countryCode) {
    case "+91": // India - 10 digits
      return /^[6-9]\d{9}$/.test(cleanPhone);
    case "+1": // US/Canada - 10 digits
      return /^[2-9]\d{9}$/.test(cleanPhone);
    case "+44": // UK - 10-11 digits
      return /^[1-9]\d{8,9}$/.test(cleanPhone);
    case "+61": // Australia - 9 digits
      return /^[2-8]\d{8}$/.test(cleanPhone);
    default:
      return cleanPhone.length >= 6 && cleanPhone.length <= 15;
  }
};

/**
 * Validates email format
 * @param {string} email - Email address
 * @returns {boolean} Is valid email
 */
export const validateEmail = (email) => {
  if (!email) return false;
  return EMAIL_PATTERN.test(email.trim());
};

/**
 * Auto-detects and formats contact input
 * @param {string} input - User input
 * @param {Object} currentData - Current form data
 * @returns {Object} Updated form data
 */
export const autoDetectContact = (input, currentData = {}) => {
  const detection = detectContactType(input);

  if (detection.type === "email") {
    return {
      ...currentData,
      contactEmail: detection.email,
      contactPhone: "",
      contactPhone_country_code: "+91",
    };
  } else if (detection.type === "phone") {
    return {
      ...currentData,
      contactPhone: detection.phone,
      contactPhone_country_code: detection.countryCode,
      contactEmail: "",
    };
  }

  return currentData;
};

/**
 * Smart input handler that auto-detects contact type
 * @param {string} fieldName - Field name (e.g., 'contactPhone', 'contactEmail')
 * @param {string} value - Input value
 * @param {Function} setFormData - Form data setter function
 * @param {Object} currentFormData - Current form data
 */
export const handleSmartContactInput = (
  fieldName,
  value,
  setFormData,
  currentFormData
) => {
  // If user is typing in a specific field, don't auto-detect
  if (fieldName === "contactPhone" || fieldName === "contactEmail") {
    setFormData({
      ...currentFormData,
      [fieldName]: value,
    });
    return;
  }

  // For generic contact field, auto-detect
  const updatedData = autoDetectContact(value, currentFormData);
  setFormData(updatedData);
};

export default {
  detectContactType,
  detectPhoneNumber,
  formatPhoneNumber,
  validatePhoneNumber,
  validateEmail,
  autoDetectContact,
  handleSmartContactInput,
};
